import { useState, useRef, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import About, { skills, links, experience } from './components/About';
import Resume from './components/Resume';
import Projects, { projects } from './components/Projects';
import Research, { research } from './components/Research';
import Blog, { posts } from './components/Blog';
import Snake from './components/Snake';
import './App.css';

const tabs = [
  { id: 'profile', label: 'profile' },
  { id: 'projects', label: 'projects' },
  { id: 'resume', label: 'resume' },
  { id: 'research', label: 'research' },
  { id: 'blog', label: 'blog' },
];

const tabComponents = {
  profile: About,
  projects: Projects,
  resume: Resume,
  research: Research,
  blog: Blog,
};

const validDirs = ['profile', 'projects', 'resume', 'research', 'blog'];

const lsContents = {
  home: validDirs.map(d => d + '/'),
  profile: ['.profile', '.experience', 'skills/', '.links'],
  projects: projects.map(p => (p.status === 'in progress' ? p.slug + '/' : p.slug)),
  resume: ['resume.pdf'],
  research: research.map(r => (r.status === 'in progress' ? r.slug + '/' : r.slug)),
  blog: posts.map((p) => p.slug + '.md'),
};

const NEOFETCH_ART = ` ██╗   ██╗ ██████╗
 ██║   ██║██╔════╝
 ██║   ██║██║
 ╚██╗ ██╔╝██║
  ╚████╔╝ ╚██████╗
   ╚═══╝   ╚═════╝`;

const WELCOME_BANNER = `██╗   ██╗ █████╗ ███╗   ██╗███████╗██╗  ██╗
██║   ██║██╔══██╗████╗  ██║██╔════╝██║  ██║
██║   ██║███████║██╔██╗ ██║███████╗███████║
╚██╗ ██╔╝██╔══██║██║╚██╗██║╚════██║██╔══██║
 ╚████╔╝ ██║  ██║██║ ╚████║███████║██║  ██║
  ╚═══╝  ╚═╝  ╚═╝╚═╝  ╚═══╝╚══════╝╚═╝  ╚═╝`;

const THEMES = {
  default: {
    '--bg': '#0a0d12', '--bar': '#0f1319', '--body-bg': '#06080c',
    '--border': 'rgba(255,255,255,0.06)', '--text': '#c0c8d4', '--bright': '#e2e8f0',
    '--dim': '#3e4756', '--dimmer': '#2a3140', '--accent': '#7a8da8',
    '--green': '#50c878', '--hazel': '#c4a35a',
  },
  dracula: {
    '--bg': '#282a36', '--bar': '#21222c', '--body-bg': '#1e1f29',
    '--border': 'rgba(255,255,255,0.08)', '--text': '#f8f8f2', '--bright': '#ffffff',
    '--dim': '#6272a4', '--dimmer': '#44475a', '--accent': '#bd93f9',
    '--green': '#50fa7b', '--hazel': '#f1fa8c',
  },
  nord: {
    '--bg': '#2e3440', '--bar': '#272c36', '--body-bg': '#242933',
    '--border': 'rgba(255,255,255,0.06)', '--text': '#d8dee9', '--bright': '#eceff4',
    '--dim': '#4c566a', '--dimmer': '#3b4252', '--accent': '#88c0d0',
    '--green': '#a3be8c', '--hazel': '#ebcb8b',
  },
  monokai: {
    '--bg': '#272822', '--bar': '#1e1f1c', '--body-bg': '#1c1d19',
    '--border': 'rgba(255,255,255,0.08)', '--text': '#f8f8f2', '--bright': '#ffffff',
    '--dim': '#75715e', '--dimmer': '#49483e', '--accent': '#66d9ef',
    '--green': '#a6e22e', '--hazel': '#e6db74',
  },
  gruvbox: {
    '--bg': '#282828', '--bar': '#1d2021', '--body-bg': '#181919',
    '--border': 'rgba(255,255,255,0.08)', '--text': '#ebdbb2', '--bright': '#fbf1c7',
    '--dim': '#665c54', '--dimmer': '#3c3836', '--accent': '#83a598',
    '--green': '#b8bb26', '--hazel': '#fabd2f',
  },
};

const themeNames = Object.keys(THEMES);

function applyTheme(name) {
  const theme = THEMES[name];
  if (!theme) return false;
  const root = document.documentElement;
  Object.entries(theme).forEach(([prop, value]) => {
    if (prop === '--body-bg') {
      document.body.style.background = value;
    } else {
      root.style.setProperty(prop, value);
    }
  });
  return true;
}

function PromptLine({ cwd, command }) {
  return (
    <div className="prompt">
      <span className="p-user">vansh</span>
      <span className="p-sep">@</span>
      <span className="p-user">portfolio</span>
      <span className="p-sep">:</span>
      <span className="p-path">{cwd}</span>
      <span className="p-char">$</span>
      {command && <span className="p-cmd">{command}</span>}
    </div>
  );
}

function HomeView({ navigateTo }) {
  return (
    <div>
      <pre className="welcome-banner">{WELCOME_BANNER}</pre>
      <div className="welcome-sub">welcome to my portfolio. type <span className="welcome-cmd">help</span> for commands. <span className="blink-cursor">█</span></div>

      <div className="prompt" style={{ marginTop: 24 }}>
        <span className="p-user">vansh</span>
        <span className="p-sep">@</span>
        <span className="p-user">portfolio</span>
        <span className="p-sep">:</span>
        <span className="p-path">~/Downloads</span>
        <span className="p-char">$</span>
        <span className="p-cmd">ls</span>
      </div>
      <div className="output">
        <div className="total-line">total {tabs.length}</div>
        {tabs.map((tab, i) => (
          <motion.div
            key={tab.id}
            className="file-row"
            role="button"
            tabIndex={0}
            onClick={() => navigateTo(tab.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigateTo(tab.id);
              }
            }}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04, duration: 0.2 }}
          >
            <span className="f-perms">drwxr-xr-x</span>
            <span className="f-size">--</span>
            <span className="f-date">2026</span>
            <span className="f-name">{tab.label}/</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default function App() {
  const [activeTab, setActiveTab] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [history, setHistory] = useState([]);
  const [commandLog, setCommandLog] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [snakeActive, setSnakeActive] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('default');
  const [clock, setClock] = useState('');

  const termBodyRef = useRef(null);
  const inputRef = useRef(null);

  const cwd = activeTab ? `~/Downloads/${activeTab}` : '~/Downloads';

  const navigateTo = useCallback((tabId) => {
    setActiveTab(tabId);
    setHistory([]);
  }, []);

  const handleLs = useCallback(() => {
    const key = activeTab || 'home';
    const items = lsContents[key] || [];
    return (
      <div className="cmd-output ls-output">
        {items.map(item => (
          <span key={item} className={item.endsWith('/') ? 'ls-dir' : 'ls-file'}>{item}</span>
        ))}
      </div>
    );
  }, [activeTab]);

  const handleHelp = useCallback(() => (
    <div className="cmd-output help-output">
      <div className="kv-line"><span className="kv-key">cat &lt;file&gt;</span>display file contents</div>
      <div className="kv-line"><span className="kv-key">cd &lt;dir&gt;</span>navigate to directory</div>
      <div className="kv-line"><span className="kv-key">cd ..</span>go up one level</div>
      <div className="kv-line"><span className="kv-key">ls</span>list directory contents</div>
      <div className="kv-line"><span className="kv-key">pwd</span>print working directory</div>
      <div className="kv-line"><span className="kv-key">clear</span>clear terminal</div>
      <div className="kv-line"><span className="kv-key">help</span>show this message</div>
      <div className="kv-line"><span className="kv-key">whoami</span>display current user</div>
      <div className="kv-line"><span className="kv-key">neofetch</span>display system info</div>
      <div className="kv-line"><span className="kv-key">history</span>show command history</div>
      <div className="kv-line"><span className="kv-key">theme &lt;name&gt;</span>switch color theme</div>
      <div className="kv-line"><span className="kv-key">snake</span>play snake game</div>
    </div>
  ), []);

  const handleNeofetch = useCallback(() => (
    <div className="cmd-output neofetch">
      <pre className="neofetch-art">{NEOFETCH_ART}</pre>
      <div className="neofetch-info">
        <div className="nf-title"><span className="nf-green">vansh</span><span className="nf-dim">@</span><span className="nf-green">portfolio</span></div>
        <div className="nf-sep">──────────────────</div>
        <div className="kv-line"><span className="kv-key">os</span>uci '28</div>
        <div className="kv-line"><span className="kv-key">role</span>robotics eng / fullstack dev</div>
        <div className="kv-line"><span className="kv-key">uptime</span>3+ years building</div>
        <div className="kv-line"><span className="kv-key">projects</span>{projects.length}</div>
        <div className="kv-line"><span className="kv-key">skills</span>react, pytorch, ros2, c++</div>
        <div className="kv-line"><span className="kv-key">shell</span>zsh</div>
        <div className="kv-line"><span className="kv-key">terminal</span>vansh-portfolio v1.0</div>
        <div className="kv-line"><span className="kv-key">theme</span>{currentTheme}</div>
        <div className="neofetch-colors">
          <span style={{background: 'var(--green)'}}></span>
          <span style={{background: 'var(--hazel)'}}></span>
          <span style={{background: 'var(--accent)'}}></span>
          <span style={{background: 'var(--bright)'}}></span>
          <span style={{background: 'var(--text)'}}></span>
          <span style={{background: 'var(--dim)'}}></span>
          <span style={{background: '#e06c75'}}></span>
          <span style={{background: '#c678dd'}}></span>
        </div>
      </div>
    </div>
  ), [currentTheme]);

  const processCommand = useCallback((raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return;

    const currentCwd = activeTab ? `~/Downloads/${activeTab}` : '~/Downloads';
    const parts = trimmed.split(/\s+/);
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1);

    setCommandLog(prev => [...prev, trimmed]);
    setHistoryIndex(-1);

    let output = null;

    switch (cmd) {
      case 'cd': {
        if (args.length === 0 || args[0] === '~' || args[0] === '/') {
          setActiveTab(null);
          setHistory([]);
          setInputValue('');
          return;
        }

        const target = args[0].replace(/\/$/, '');

        if (target === '..') {
          if (activeTab !== null) {
            setActiveTab(null);
            setHistory([]);
            setInputValue('');
            return;
          }
          setInputValue('');
          return;
        }

        if (target.startsWith('../') && activeTab !== null) {
          const sibling = target.slice(3).replace(/\/$/, '');
          if (validDirs.includes(sibling)) {
            setActiveTab(sibling);
            setHistory([]);
            setInputValue('');
            return;
          }
          output = <div className="cmd-output cmd-error">cd: no such directory: {target}</div>;
          break;
        }

        if (activeTab === null && validDirs.includes(target)) {
          setActiveTab(target);
          setHistory([]);
          setInputValue('');
          return;
        }

        if (activeTab === null && target.startsWith('Downloads/')) {
          const sub = target.slice('Downloads/'.length).replace(/\/$/, '');
          if (validDirs.includes(sub)) {
            setActiveTab(sub);
            setHistory([]);
            setInputValue('');
            return;
          }
        }

        output = <div className="cmd-output cmd-error">cd: no such directory: {target}</div>;
        break;
      }
      case 'ls':
        output = handleLs();
        break;
      case 'pwd':
        output = <div className="cmd-output">{currentCwd}</div>;
        break;
      case 'clear':
        setHistory([]);
        setInputValue('');
        return;
      case 'help':
        output = handleHelp();
        break;
      case 'whoami':
        output = <div className="cmd-output">vansh</div>;
        break;
      case 'neofetch':
        output = handleNeofetch();
        break;
      case 'cat':
      case 'open': {
        if (args.length === 0) {
          output = <div className="cmd-output cmd-error">{cmd}: missing file operand</div>;
          break;
        }
        const file = args[0];
        if (activeTab === 'profile') {
          if (file === '.profile') {
            output = (
              <div className="cmd-output">
                <div className="kv-line"><span className="kv-key">name</span>vansh chhabra</div>
                <div className="kv-line"><span className="kv-key">role</span>robotics engineer · fullstack developer</div>
                <div className="kv-line"><span className="kv-key">school</span>uci</div>
                <div className="term-text">
                  <p>hi, i'm vansh — i build things in robotics and fullstack. focused on SLAM, control systems, and pcb design. i also make and ship web apps.</p>
                  <p>outside of coding, probably listening to music, playing basketball, or hanging with friends.</p>
                </div>
              </div>
            );
          } else if (file === '.links') {
            output = (
              <div className="cmd-output">
                {links.map((link) => (
                  <div key={link.label} className="link-row">
                    <span className="link-label">{link.label}</span>
                    <a href={link.url} target="_blank" rel="noopener noreferrer">
                      {link.url.replace('mailto:', '')}
                    </a>
                  </div>
                ))}
              </div>
            );
          } else if (file === 'skills/' || file === 'skills') {
            output = (
              <div className="cmd-output">
                {Object.entries(skills).map(([category, items]) => (
                  <div key={category} className="dir-block">
                    <div className="dir-label">{category}/</div>
                    <div className="skill-list">{items.join('  ')}</div>
                  </div>
                ))}
              </div>
            );
          } else if (file === '.experience') {
            output = (
              <div className="cmd-output">
                {experience.map((job) => (
                  <div key={job.org} className="exp-block">
                    <div className="exp-head">
                      <span className="exp-role">{job.role}</span>
                      <span className="exp-at"> @ </span>
                      <span className="exp-org">{job.org}</span>
                      <span className="exp-date">{job.date}</span>
                    </div>
                    <div className="exp-meta">{job.meta}</div>
                    <div className="exp-detail">{job.detail}</div>
                  </div>
                ))}
              </div>
            );
          } else {
            output = <div className="cmd-output cmd-error">{cmd}: {file}: no such file</div>;
          }
        } else if (activeTab === 'projects') {
          const project = projects.find(p => p.slug === file || p.slug === file.replace(/\/$/, ''));
          if (project) {
            output = (
              <div className="cmd-output">
                <div className="project-detail">
                  <div className="content-title">{project.title}</div>
                  <div className="content-meta">
                    <span>{project.date}</span>
                    <span className="sep">·</span>
                    <span>{project.status}</span>
                  </div>
                  <div className="project-desc" style={{ marginTop: 16 }}>{project.description}</div>
                  <div style={{ marginBottom: 12 }}>
                    {project.tech.map((t) => <span key={t} className="tech-tag">{t}</span>)}
                  </div>
                  {project.link && (
                    <a href={project.link} target="_blank" rel="noopener noreferrer" className="project-link">
                      {project.link}
                    </a>
                  )}
                </div>
              </div>
            );
          } else {
            output = <div className="cmd-output cmd-error">{cmd}: {file}: no such file</div>;
          }
        } else if (activeTab === 'resume') {
          if (file === 'resume.pdf') {
            output = (
              <div className="cmd-output">
                <iframe src="/resume.pdf#toolbar=0&navpanes=0&scrollbar=0" title="Resume" className="resume-frame" />
                <div style={{ marginTop: 12, fontSize: 13 }}>
                  <span style={{ color: 'var(--dim)' }}>download → </span>
                  <a href="/resume.pdf" download className="download-link">resume.pdf</a>
                </div>
              </div>
            );
          } else {
            output = <div className="cmd-output cmd-error">{cmd}: {file}: no such file</div>;
          }
        } else if (activeTab === 'research') {
          const item = research.find(r => r.slug === file || r.slug === file.replace(/\/$/, ''));
          if (item) {
            output = (
              <div className="cmd-output">
                <div className="project-detail">
                  <div className="content-title">{item.title}</div>
                  <div className="content-meta">
                    <span>{item.lab}</span>
                    <span className="sep">·</span>
                    <span>{item.date}</span>
                    <span className="sep">·</span>
                    <span>{item.status}</span>
                  </div>
                  <div className="project-desc" style={{ marginTop: 16 }}>{item.description}</div>
                  <div style={{ marginBottom: 12 }}>
                    {item.tech.map((t) => <span key={t} className="tech-tag">{t}</span>)}
                  </div>
                </div>
              </div>
            );
          } else {
            output = <div className="cmd-output cmd-error">{cmd}: {file}: no such file</div>;
          }
        } else if (activeTab === 'blog') {
          const post = posts.find(p => p.slug === file || p.slug === file.replace(/\.md$/, ''));
          if (post) {
            output = (
              <div className="cmd-output">
                <div className="content-header">
                  <div className="content-title">{post.title}</div>
                  <div className="content-meta"><span>{post.date}</span></div>
                </div>
                <div className="content-body">
                  {post.body.map((para, i) => <p key={i}>{para}</p>)}
                </div>
              </div>
            );
          } else {
            output = <div className="cmd-output cmd-error">{cmd}: {file}: no such file</div>;
          }
        } else {
          // home directory — items are directories, not files
          if (validDirs.includes(file.replace(/\/$/, ''))) {
            output = <div className="cmd-output cmd-error">{cmd}: {file}: is a directory</div>;
          } else {
            output = <div className="cmd-output cmd-error">{cmd}: {file}: no such file</div>;
          }
        }
        break;
      }
      case 'history': {
        // commandLog already includes the current 'history' command from setCommandLog above,
        // but since setState is async we read the current snapshot + this command
        const allCmds = [...commandLog, trimmed];
        output = (
          <div className="cmd-output history-output">
            {allCmds.map((c, i) => (
              <div key={i} className="history-line">
                <span className="history-num">{i + 1}</span>{c}
              </div>
            ))}
          </div>
        );
        break;
      }
      case 'theme': {
        if (args.length === 0 || args[0] === 'list') {
          output = (
            <div className="cmd-output">
              <div style={{ marginBottom: 8 }}>current: <span style={{ color: 'var(--green)' }}>{currentTheme}</span></div>
              <div style={{ color: 'var(--dim)' }}>available themes:</div>
              <div className="ls-output" style={{ marginTop: 4 }}>
                {themeNames.map(t => (
                  <span key={t} className={t === currentTheme ? 'ls-dir' : 'ls-file'}>{t}</span>
                ))}
              </div>
            </div>
          );
          break;
        }
        const themeName = args[0].toLowerCase();
        if (applyTheme(themeName)) {
          setCurrentTheme(themeName);
          output = <div className="cmd-output">switched to <span style={{ color: 'var(--green)' }}>{themeName}</span> theme</div>;
        } else {
          output = (
            <div className="cmd-output cmd-error">
              unknown theme: {themeName}. available: {themeNames.join(', ')}
            </div>
          );
        }
        break;
      }
      case 'snake':
        setSnakeActive(true);
        setHistory([]);
        setInputValue('');
        return;
      default:
        output = <div className="cmd-output cmd-error">command not found: {cmd}</div>;
        break;
    }

    setHistory(prev => [...prev, { cwd: currentCwd, command: trimmed, output }]);
    setInputValue('');
  }, [activeTab, handleLs, handleHelp, handleNeofetch, commandLog, currentTheme]);

  const handleTabComplete = useCallback(() => {
    const parts = inputValue.split(/\s+/);
    const lastWord = parts[parts.length - 1] || '';

    const commands = ['cat', 'cd', 'clear', 'help', 'history', 'ls', 'neofetch', 'open', 'pwd', 'snake', 'theme', 'whoami'];
    const targets = activeTab === null ? validDirs : ['..'];

    // If typing 'theme <partial>', complete theme names
    if (parts.length === 2 && parts[0].toLowerCase() === 'theme') {
      const matches = themeNames.filter(t => t.startsWith(lastWord));
      if (matches.length === 1) {
        parts[1] = matches[0];
        setInputValue(parts.join(' '));
      }
      return;
    }

    // If typing 'cat/open <partial>', complete filenames in current dir
    if (parts.length === 2 && (parts[0].toLowerCase() === 'cat' || parts[0].toLowerCase() === 'open')) {
      const key = activeTab || 'home';
      const files = lsContents[key] || [];
      const matches = files.filter(f => f.startsWith(lastWord));
      if (matches.length === 1) {
        parts[1] = matches[0];
        setInputValue(parts.join(' '));
      }
      return;
    }

    const options = parts.length <= 1 ? commands : targets;
    const matches = options.filter(o => o.startsWith(lastWord));

    if (matches.length === 1) {
      parts[parts.length - 1] = matches[0];
      setInputValue(parts.join(' '));
    }
  }, [inputValue, activeTab]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      processCommand(inputValue);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const newIndex = historyIndex === -1 ? commandLog.length - 1 : Math.max(0, historyIndex - 1);
      if (commandLog[newIndex]) {
        setHistoryIndex(newIndex);
        setInputValue(commandLog[newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex === -1) return;
      const newIndex = historyIndex + 1;
      if (newIndex >= commandLog.length) {
        setHistoryIndex(-1);
        setInputValue('');
      } else {
        setHistoryIndex(newIndex);
        setInputValue(commandLog[newIndex]);
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      handleTabComplete();
    } else if (e.key === 'l' && e.ctrlKey) {
      e.preventDefault();
      setHistory([]);
    }
  }, [inputValue, processCommand, commandLog, historyIndex, handleTabComplete]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (termBodyRef.current) {
      termBodyRef.current.scrollTop = termBodyRef.current.scrollHeight;
    }
  }, [history, activeTab]);

  // Live clock in the status bar
  useEffect(() => {
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      const d = new Date();
      setClock(`${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const ActiveComponent = activeTab ? tabComponents[activeTab] : null;

  return (
    <>
    <div className="bg-fx" aria-hidden="true" />
    <div className="terminal">
      {/* Title Bar */}
      <div className="bar">
        <div className="bar-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <div className="bar-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={activeTab === tab.id ? 'active' : ''}
              onClick={() => navigateTo(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="bar-title">vansh@portfolio</div>
      </div>

      {/* Body */}
      <div
        className="term-body"
        ref={termBodyRef}
        onClick={() => !snakeActive && inputRef.current?.focus()}
      >
        {snakeActive ? (
          <Snake onExit={() => setSnakeActive(false)} />
        ) : (
          <>
            {/* Current view */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab ?? 'home'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.12 }}
              >
                {activeTab === null ? (
                  <HomeView navigateTo={navigateTo} />
                ) : (
                  <ActiveComponent />
                )}
              </motion.div>
            </AnimatePresence>

            {/* Command history */}
            {history.map((entry, i) => (
              <div key={i}>
                <PromptLine cwd={entry.cwd} command={entry.command} />
                {entry.output}
              </div>
            ))}

            {/* Interactive input */}
            <div className="prompt input-prompt">
              <span className="p-user">vansh</span>
              <span className="p-sep">@</span>
              <span className="p-user">portfolio</span>
              <span className="p-sep">:</span>
              <span className="p-path">{cwd}</span>
              <span className="p-char">$</span>
              <input
                ref={inputRef}
                type="text"
                className="term-input"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                spellCheck={false}
                autoComplete="off"
                autoFocus
              />
            </div>
          </>
        )}
      </div>

      {/* Status Bar */}
      <div className="status">
        <span className="status-left">
          <span className="status-dot" />
          {snakeActive ? 'snake' : 'vansh-portfolio'}
        </span>
        <span className="status-right">
          <span>{snakeActive ? 'press q or esc to quit' : cwd}</span>
          <span className="status-clock">{clock}</span>
        </span>
      </div>
    </div>
    </>
  );
}
